import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QrCode, LogOut, Plus, ShoppingBag, Gift, Search, Camera, X, Send } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { staffApi } from "../lib/api";
import { CraftLogo } from "../components/Shared";
import { useLang } from "../i18n";

function TelegramConnect() {
  const [tg, setTg] = useState(null);
  const loadTg = () => staffApi.post("/staff/telegram-link").then((r) => setTg(r.data)).catch(() => {});
  useEffect(() => {
    loadTg();
    const iv = setInterval(loadTg, 15000);
    return () => clearInterval(iv);
  }, []);
  if (!tg) return null;
  return (
    <div className="bg-white/10 rounded-2xl p-3.5 flex items-center gap-3" data-testid="staff-tg-connect">
      <Send size={18} className="text-[#229ED9] shrink-0" />
      {tg.connected ? (
        <div className="text-xs font-bold text-green-400 flex-1" data-testid="staff-tg-connected">
          Telegram подключён{tg.telegram_username ? ` (@${tg.telegram_username})` : ""} — вы получаете запросы на погашение сертификатов
        </div>
      ) : (
        <>
          <div className="text-xs font-bold text-gray-300 flex-1">Подключите Telegram, чтобы подтверждать погашение сертификатов</div>
          {tg.link ? (
            <a data-testid="staff-tg-connect-btn" href={tg.link} target="_blank" rel="noreferrer"
              className="shrink-0 px-3.5 py-2 rounded-xl bg-[#229ED9] text-white text-xs font-black">Подключить</a>
          ) : (
            <span className="text-[10px] font-bold text-orange-400 shrink-0">Бот не настроен</span>
          )}
        </>
      )}
    </div>
  );
}

export default function StaffApp() {
  const { L, t } = useLang();
  const [token, setToken] = useState(localStorage.getItem("craft_staff_token"));
  const [name, setName] = useState(localStorage.getItem("craft_staff_name") || "");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [profile, setProfile] = useState(null);
  const [cert, setCert] = useState(null);
  const [confirmRedeem, setConfirmRedeem] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (e) {}
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("staff-qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          async (decoded) => {
            let clean = (decoded || "").trim().toUpperCase();
            const certMatch = clean.match(/\/CERT\/([A-Z0-9-]+)/);
            if (certMatch) clean = certMatch[1];
            setCode(clean);
            await stopScanner();
            toast.success(`QR: ${clean}`);
            lookupCode(clean);
          },
          () => {}
        );
      } catch (e) {
        toast.error("Камера недоступна. Введите код вручную");
        setScanning(false);
        scannerRef.current = null;
      }
    }, 100);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const login = async () => {
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/login", { secret, name: name || "Staff" });
      localStorage.setItem("craft_staff_token", data.token);
      localStorage.setItem("craft_staff_name", data.name);
      setToken(data.token);
      toast.success("Вход выполнен");
    } catch (e) { toast.error("Неверный секретный код"); }
    setBusy(false);
  };
  const logout = () => { localStorage.removeItem("craft_staff_token"); setToken(null); setProfile(null); };

  const lookupCode = async (c) => {
    const val = (c || code).trim().toUpperCase();
    if (val.startsWith("GC-")) return certLookup(val);
    setBusy(true);
    try {
      const { data } = await staffApi.get(`/staff/customer/${val}`);
      setProfile(data); setCert(null);
    } catch (e) {
      // maybe cashier typed a certificate code without the GC- prefix
      if (/^[A-Z0-9]{6,10}$/.test(val)) {
        try {
          const { data } = await staffApi.get(`/staff/certificate/GC-${val}`);
          setCert(data); setProfile(null); setConfirmRedeem(false); setBusy(false);
          return;
        } catch (e2) { /* not a certificate either */ }
      }
      toast.error("Клиент не найден"); setProfile(null);
    }
    setBusy(false);
  };
  const lookup = () => lookupCode(code);

  const certLookup = async (val) => {
    setBusy(true);
    try {
      const { data } = await staffApi.get(`/staff/certificate/${val}`);
      setCert(data); setProfile(null); setConfirmRedeem(false);
    } catch (e) { toast.error("Сертификат не найден"); setCert(null); }
    setBusy(false);
  };
  const certRedeem = async () => {
    setBusy(true);
    try {
      const { data } = await staffApi.post(`/staff/certificate/${cert.code}/redeem`);
      setCert(data);
      setConfirmRedeem(false);
      toast.success(`Сертификат списан: ${(data.amount || 0).toLocaleString("ru-RU")} сум`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "already_redeemed" ? "Уже использован" : d === "not_activated" ? "Сертификат не активирован в Telegram — списание недоступно" : d === "expired" ? "Срок сертификата истёк" : "Ошибка погашения");
    }
    setBusy(false);
  };
  const verify = async (action) => {
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/verify", { code: code.trim(), action });
      setProfile(data.profile);
      if (data.unlocked?.length) toast.success("🎁 Награда разблокирована!");
      else toast.success(action === "visit" ? "+1 посещение" : "+1 покупка");
    } catch (e) {
      const d = e.response?.data?.detail || "";
      toast.error(d.startsWith("min_interval") ? `Минимальный интервал: ${d.split(":")[1]} ч.` : "Ошибка");
    }
    setBusy(false);
  };
  const redeem = async (campaignId) => {
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/redeem", { code: code.trim(), campaign_id: campaignId });
      setProfile(data.profile);
      toast.success("Награда выдана ✓");
    } catch (e) { toast.error("Награда недоступна"); }
    setBusy(false);
  };

  const confirmReview = async (claimId) => {
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/confirm-review", { code: code.trim(), claim_id: claimId });
      setProfile(data.profile);
      toast.success("Отзыв подтверждён — подарок выдан");
    } catch (e) { toast.error("Не удалось подтвердить"); }
    setBusy(false);
  };

  if (!token)
    return (
      <div className="min-h-screen bg-[#111] flex items-center justify-center p-5">
        <div className="w-full max-w-[380px] bg-white rounded-3xl p-6 space-y-4">
          <CraftLogo />
          <h1 className="font-display font-black text-xl">Staff Verification</h1>
          <input data-testid="staff-name-input" className="craft-input" placeholder="Ваше имя" value={name} onChange={(e) => setName(e.target.value)} />
          <input data-testid="staff-secret-input" className="craft-input" type="password" placeholder="Секретный код (CRAFT-2026)" value={secret} onChange={(e) => setSecret(e.target.value)} />
          <button data-testid="staff-login-btn" className="craft-btn" disabled={busy || !secret} onClick={login}>Войти</button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#111] text-white">
      <div className="max-w-[430px] mx-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><QrCode className="text-[#FEBC10]" /><span className="font-display font-black">CRAFT Staff</span></div>
          <button data-testid="staff-logout-btn" onClick={logout} className="p-2 bg-white/10 rounded-xl"><LogOut size={16} /></button>
        </div>
        <TelegramConnect />
        <div className="bg-white/10 rounded-3xl p-5 space-y-3">
          <label className="text-xs font-bold uppercase text-gray-400">Код клиента или сертификата</label>
          {scanning && (
            <div className="rounded-2xl overflow-hidden bg-black relative" data-testid="staff-qr-scanner">
              <div id="staff-qr-reader" className="w-full" />
              <button data-testid="staff-scan-stop-btn" onClick={stopScanner}
                className="absolute top-2 right-2 z-10 p-2 bg-black/60 rounded-xl"><X size={16} /></button>
            </div>
          )}
          {!scanning && (
            <button data-testid="staff-scan-btn" className="craft-btn craft-btn-red" onClick={startScanner}>
              <Camera size={18} /> {t("scan_qr")}
            </button>
          )}
          <input data-testid="staff-code-input" className="craft-input text-black uppercase tracking-widest font-black text-center text-xl"
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" />
          <button data-testid="staff-lookup-btn" className="craft-btn" disabled={busy || code.length < 4} onClick={lookup}><Search size={18} /> Найти клиента</button>
        </div>
        {cert && (
          <div className="bg-white rounded-3xl p-5 text-black space-y-3" data-testid="staff-cert-panel">
            <div className="flex items-center gap-2">
              <Gift size={20} className="text-[#FF0000]" />
              <div className="font-display font-black text-lg">Сертификат <span className="text-gray-400 text-sm">{cert.code}</span></div>
            </div>
            <div className="font-display font-black text-3xl text-[#FF0000]" data-testid="staff-cert-amount">{(cert.amount || 0).toLocaleString("ru-RU")} сум</div>
            {cert.status === "active" && !cert.activated && (
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3 space-y-1" data-testid="staff-cert-not-activated">
                <div className="text-sm font-bold text-orange-600">⚠ Не активирован в Telegram</div>
                <div className="text-xs font-semibold text-gray-500">
                  Гость не активировал сертификат (QR → Telegram). Списание возможно, но лучше напомнить гостю про активацию.
                  {cert.activation_days ? ` Срок (${cert.activation_days} дн.) начнёт отсчитываться после активации.` : ""}
                </div>
              </div>
            )}
            {cert.status === "active" && !confirmRedeem && (
              <>
                <span className="inline-block px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold" data-testid="staff-cert-status">✓ Действителен{cert.expires ? ` до ${cert.expires}` : ""}</span>
                <button data-testid="staff-cert-redeem-btn" className="craft-btn craft-btn-red" disabled={busy} onClick={() => setConfirmRedeem(true)}>
                  <Gift size={17} /> Списать сертификат
                </button>
              </>
            )}
            {cert.status === "active" && confirmRedeem && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3" data-testid="staff-cert-confirm">
                <div className="font-bold text-sm text-center">Списать сертификат <b>{cert.code}</b> на {(cert.amount || 0).toLocaleString("ru-RU")} сум?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="staff-cert-confirm-yes" className="craft-btn craft-btn-red text-sm" disabled={busy} onClick={certRedeem}>Списать</button>
                  <button data-testid="staff-cert-confirm-no" className="craft-btn craft-btn-ghost text-sm" disabled={busy} onClick={() => setConfirmRedeem(false)}>Отмена</button>
                </div>
              </div>
            )}
            {cert.status === "redeemed" && (
              <div className="bg-gray-100 rounded-2xl p-3 text-sm font-bold text-gray-500" data-testid="staff-cert-status">
                Использован {cert.redeemed_at ? `· ${new Date(cert.redeemed_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""} {cert.redeemed_via === "staff" ? "(кассиром)" : cert.redeemed_via === "telegram" ? "(Telegram)" : "(секретным кодом)"}
              </div>
            )}
            {cert.status === "void" && <div className="bg-red-50 rounded-2xl p-3 text-sm font-bold text-red-600" data-testid="staff-cert-status">Аннулирован</div>}
          </div>
        )}
        {profile && (
          <div className="bg-white rounded-3xl p-5 text-black space-y-4" data-testid="staff-profile">
            <div>
              <div className="font-display font-black text-xl">{profile.first_name || "Гость"} <span className="text-gray-400 text-sm">#{profile.personal_code}</span></div>
              <div className="text-sm text-gray-500 font-semibold">Посещений: {profile.total_visits} · Покупок: {profile.total_purchases}</div>
            </div>
            {(profile.loyalty || []).map((l) => (
              <div key={l.campaign_id} className="bg-gray-50 rounded-2xl p-4 space-y-2">
                <div className="font-bold text-sm">{L(l.name)}</div>
                <div className="flex gap-1">
                  {Array.from({ length: l.required }).map((_, i) => (
                    <div key={i} className={`h-2 flex-1 rounded-full ${i < l.count ? "bg-[#FEBC10]" : "bg-gray-200"}`} />
                  ))}
                </div>
                <div className="text-xs font-bold text-gray-500">{l.count} / {l.required} · {l.reward_status === "available" ? "🎁 Награда доступна" : l.reward_status === "redeemed" ? "Использована" : "В процессе"}</div>
                {l.reward_status === "available" && (
                  <button data-testid={`staff-redeem-btn-${l.campaign_id}`} className="craft-btn craft-btn-red text-sm" disabled={busy} onClick={() => redeem(l.campaign_id)}>
                    <Gift size={16} /> Выдать награду: {L(l.reward?.name)}
                  </button>
                )}
              </div>
            ))}
            {(profile.review_claims || []).map((cl) => (
              <div key={cl.id} className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 space-y-2" data-testid={`staff-claim-${cl.id}`}>
                <div className="font-bold text-sm">Отзыв: {cl.campaign_name} <span className="text-gray-400">({cl.platform || "—"})</span></div>
                <div className="text-xs text-gray-500 font-semibold">Подарок: {cl.reward?.name?.ru || "—"}</div>
                <button data-testid={`staff-confirm-review-${cl.id}`} className="craft-btn text-sm" disabled={busy} onClick={() => confirmReview(cl.id)}>
                  <Gift size={16} /> Подтвердить отзыв и выдать подарок
                </button>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="staff-add-visit-btn" className="craft-btn text-sm" disabled={busy} onClick={() => verify("visit")}><Plus size={16} /> Посещение</button>
              <button data-testid="staff-add-purchase-btn" className="craft-btn craft-btn-ghost text-sm" disabled={busy} onClick={() => verify("purchase")}><ShoppingBag size={16} /> Покупка</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
